import { lazy, Suspense } from 'react'
import { BrowserRouter, Routes, Route } from 'react-router-dom'
import { Spinner, Flex } from '@chakra-ui/react'
import { AuthProvider } from './hooks/useAuth.js'
import { PreviewModeProvider } from './hooks/usePreviewMode.js'
import { ProfileModalProvider } from './hooks/useProfileModal.js'
import { LanguageRedirect } from './components/LanguageRedirect.js'
import { LanguageRouter } from './components/LanguageRouter.js'
import { LearnShell } from './components/LearnShell.js'
import { PublicShell } from './components/PublicShell.js'
import { RequireAuth } from './components/RequireAuth.js'
import { NotFoundView } from './components/NotFoundView.js'

// Lazy-loaded pages — each becomes a separate chunk.
// The shell (React, Router, Chakra, auth, i18n) loads immediately;
// page code loads only when the user navigates to that route.
const LearnHomePage = lazy(() => import('./pages/LearnHomePage.js').then(m => ({ default: m.LearnHomePage })))
const LearnCoursePage = lazy(() => import('./pages/LearnCoursePage.js').then(m => ({ default: m.LearnCoursePage })))
const LearnTrackPage = lazy(() => import('./pages/LearnTrackPage.js').then(m => ({ default: m.LearnTrackPage })))
const LearnModulePage = lazy(() => import('./pages/LearnModulePage.js').then(m => ({ default: m.LearnModulePage })))
const LessonPlayerPage = lazy(() => import('./pages/LessonPlayerPage.js').then(m => ({ default: m.LessonPlayerPage })))
const LearnLoginPage = lazy(() => import('./pages/LearnLoginPage.js').then(m => ({ default: m.LearnLoginPage })))
const ConceptMapPage = lazy(() => import('./pages/ConceptMapPage.js').then(m => ({ default: m.ConceptMapPage })))
const VerifyCertificatePage = lazy(() => import('./pages/VerifyCertificatePage.js').then(m => ({ default: m.VerifyCertificatePage })))
const InvitationPage = lazy(() => import('./pages/InvitationPage.js').then(m => ({ default: m.InvitationPage })))

// Org dashboard pages (lazy)
const OrgShell = lazy(() => import('./components/org/OrgShell.js').then(m => ({ default: m.OrgShell })))
const OrgDashboardPage = lazy(() => import('./pages/org/OrgDashboardPage.js').then(m => ({ default: m.OrgDashboardPage })))
const OrgMembersPage = lazy(() => import('./pages/org/OrgMembersPage.js').then(m => ({ default: m.OrgMembersPage })))
const OrgMemberDetailPage = lazy(() => import('./pages/org/OrgMemberDetailPage.js').then(m => ({ default: m.OrgMemberDetailPage })))
const OrgCoursesPage = lazy(() => import('./pages/org/OrgCoursesPage.js').then(m => ({ default: m.OrgCoursesPage })))
const OrgCourseAnalyticsPage = lazy(() => import('./pages/org/OrgCourseAnalyticsPage.js').then(m => ({ default: m.OrgCourseAnalyticsPage })))
const OrgClassesPage = lazy(() => import('./pages/org/OrgClassesPage.js').then(m => ({ default: m.OrgClassesPage })))

function PageLoader() {
  return (
    <Flex justify="center" align="center" minH="40vh">
      <Spinner color="acp.blue" size="lg" />
    </Flex>
  )
}

export function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <PreviewModeProvider>
          <ProfileModalProvider>
            <Suspense fallback={<PageLoader />}>
              <Routes>
                <Route index element={<LanguageRedirect />} />
                <Route path=":lang" element={<LanguageRouter />}>
                  {/* Public within a language: reachable WITHOUT a session.
                      Certificate verification (third parties) + invitation
                      landings (invitees with no account yet). Minimal chrome,
                      no auth controls. */}
                  <Route element={<PublicShell />}>
                    <Route path="verify/:code" element={<VerifyCertificatePage />} />
                    <Route path="invitations/:token" element={<InvitationPage />} />
                  </Route>
                  {/* Open-access learning surface: catalog + lessons render for
                      ANONYMOUS visitors (no redirect). Each page gates its own
                      content — a register banner replaces progress when logged
                      out, and the lesson player shows a register wall / paywall
                      per the lesson's access tier (registerLocked / licenseLocked
                      from the API). `public`-tier lessons are playable logged out;
                      `auth`/`paid` lessons surface the appropriate prompt. */}
                  <Route element={<LearnShell />}>
                    <Route index element={<LearnHomePage />} />
                    <Route path="courses/:courseId" element={<LearnCoursePage />} />
                    <Route path="courses/:courseId/map" element={<ConceptMapPage />} />
                    <Route path="courses/:courseId/tracks/:trackId" element={<LearnTrackPage />} />
                    <Route path="lessons/:lessonId/:moduleId?" element={<LessonPlayerPage />} />
                    {/* Standalone module viewer stays login-gated — its backend
                        endpoint (GET /modules/:id) is NOT part of the open-access
                        set, so an anonymous hit would 401. Redirect to login. */}
                    <Route element={<RequireAuth />}>
                      <Route path="modules/:moduleId" element={<LearnModulePage />} />
                    </Route>
                    {/* Unknown path under a valid language → localized 404 inside the shell */}
                    <Route path="*" element={<NotFoundView />} />
                  </Route>
                  {/* Org dashboard stays login-gated: a visitor with no session is
                      bounced to /login?returnTo=… */}
                  <Route element={<RequireAuth />}>
                    <Route path="org/:orgId" element={<OrgShell />}>
                      <Route path="dashboard" element={<OrgDashboardPage />} />
                      <Route path="members" element={<OrgMembersPage />} />
                      <Route path="members/:userId" element={<OrgMemberDetailPage />} />
                      <Route path="courses" element={<OrgCoursesPage />} />
                      <Route path="courses/:courseId" element={<OrgCourseAnalyticsPage />} />
                      <Route path="classes" element={<OrgClassesPage />} />
                    </Route>
                  </Route>
                </Route>
                <Route path="login" element={<LearnLoginPage />} />
                {/* Any other top-level path → detect language and redirect home */}
                <Route path="*" element={<LanguageRedirect />} />
              </Routes>
            </Suspense>
          </ProfileModalProvider>
        </PreviewModeProvider>
      </AuthProvider>
    </BrowserRouter>
  )
}
